"use client";
import { NavMenuProvider } from "@/components/NavMenuContext";
import { components } from "@/slices";
import { PrismicImage, PrismicLink, SliceZone } from "@prismicio/react";
import React, { useEffect, useMemo, useRef, useState } from "react";

export default function Header({ logo, slices, cta }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSubmenus, setExpandedSubmenus] = useState({});
  const mobileMenuRef = useRef(null);

  const mobileSlices = useMemo(() => slices ?? [], [slices]);

  const toggleSubmenu = (index) => {
    setExpandedSubmenus((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setExpandedSubmenus({});
  };

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handlePointerDown = (event) => {
      if (!mobileMenuRef.current?.contains(event.target)) {
        closeMobileMenu();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="w-full overflow-visible py-2 lg:py-1 bg-bleu-100 padding-x text-bleu-900">
      <div className="flex items-center justify-between gap-3">
        <a href="/"><PrismicImage field={logo} className="w-[80px] h-[46px] lg:w-[120px] lg:h-[70px] object-contain" /></a>
        <NavMenuProvider>
          <ul className="hidden cursor-pointer w-full items-center overflow-visible md:flex gap-3 md:items-end md:justify-end">
            <SliceZone slices={slices} components={components} />
          </ul>
        </NavMenuProvider>
        <PrismicLink field={cta} className="cta cta-terracota-400 hidden body-s px-5 md:inline-flex" />
        <button
          type="button"
          aria-label="Ouvrir le menu"
          onClick={() => setIsMobileMenuOpen(true)}
          className="inline-flex h-[13.5px] w-[18px] items-center justify-center rounded-md md:hidden"
        >
          <span className="sr-only">Menu</span>
          <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6.75C18 6.94891 17.921 7.13968 17.7803 7.28033C17.6397 7.42098 17.4489 7.5 17.25 7.5H0.75C0.551088 7.5 0.360322 7.42098 0.21967 7.28033C0.0790178 7.13968 0 6.94891 0 6.75C0 6.55109 0.0790178 6.36032 0.21967 6.21967C0.360322 6.07902 0.551088 6 0.75 6H17.25C17.4489 6 17.6397 6.07902 17.7803 6.21967C17.921 6.36032 18 6.55109 18 6.75ZM0.75 1.5H17.25C17.4489 1.5 17.6397 1.42098 17.7803 1.28033C17.921 1.13968 18 0.948912 18 0.75C18 0.551088 17.921 0.360322 17.7803 0.21967C17.6397 0.0790178 17.4489 0 17.25 0H0.75C0.551088 0 0.360322 0.0790178 0.21967 0.21967C0.0790178 0.360322 0 0.551088 0 0.75C0 0.948912 0.0790178 1.13968 0.21967 1.28033C0.360322 1.42098 0.551088 1.5 0.75 1.5ZM17.25 12H0.75C0.551088 12 0.360322 12.079 0.21967 12.2197C0.0790178 12.3603 0 12.5511 0 12.75C0 12.9489 0.0790178 13.1397 0.21967 13.2803C0.360322 13.421 0.551088 13.5 0.75 13.5H17.25C17.4489 13.5 17.6397 13.421 17.7803 13.2803C17.921 13.1397 18 12.9489 18 12.75C18 12.5511 17.921 12.3603 17.7803 12.2197C17.6397 12.079 17.4489 12 17.25 12Z" fill="#144154" />
          </svg>
        </button>
      </div>
      <div
        className={`fixed inset-0 z-[70] transition-transform duration-300 ease-out md:hidden ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        aria-hidden={!isMobileMenuOpen}
      >
        <div ref={mobileMenuRef} className="flex w-screen bg-bleu-50 flex-col overflow-y-auto px-6 py-6">
          <div className="mb-8 flex items-end justify-end">
            <button
              type="button"
              aria-label="Fermer le menu"
              onClick={closeMobileMenu}
              className="inline-flex h-4 w-4 items-center justify-center rounded-md"
            >
              <span className="sr-only">Fermer</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.7656 14.6336C15.8399 14.708 15.8988 14.7962 15.9391 14.8933C15.9793 14.9904 16 15.0945 16 15.1996C16 15.3047 15.9793 15.4088 15.9391 15.5059C15.8988 15.603 15.8399 15.6912 15.7656 15.7656C15.6912 15.8399 15.603 15.8988 15.5059 15.9391C15.4088 15.9793 15.3047 16 15.1996 16C15.0945 16 14.9904 15.9793 14.8933 15.9391C14.7962 15.8988 14.708 15.8399 14.6336 15.7656L8 9.13094L1.36637 15.7656C1.21626 15.9157 1.01268 16 0.8004 16C0.588121 16 0.384536 15.9157 0.234432 15.7656C0.0843276 15.6155 4.18453e-09 15.4119 0 15.1996C-4.18453e-09 14.9873 0.0843276 14.7837 0.234432 14.6336L6.86906 8L0.234432 1.36637C0.0843276 1.21626 -1.5816e-09 1.01268 0 0.8004C1.5816e-09 0.588121 0.0843276 0.384536 0.234432 0.234432C0.384536 0.0843276 0.588121 1.5816e-09 0.8004 0C1.01268 -1.5816e-09 1.21626 0.0843276 1.36637 0.234432L8 6.86906L14.6336 0.234432C14.7837 0.0843276 14.9873 -4.18453e-09 15.1996 0C15.4119 4.18453e-09 15.6155 0.0843276 15.7656 0.234432C15.9157 0.384536 16 0.588121 16 0.8004C16 1.01268 15.9157 1.21626 15.7656 1.36637L9.13094 8L15.7656 14.6336Z" fill="#0C2631" />
              </svg>
            </button>
          </div>

          <nav aria-label="Menu mobile">
            <ul className="flex flex-col gap-2">
              {mobileSlices.map((slice, index) => {
                if (slice.variation === "subMenu") {
                  return (
                    <li key={`${slice.id ?? "menu"}-${index}`} className="rounded-lg px-1 py-1">
                      <button
                        type="button"
                        onClick={() => toggleSubmenu(index)}
                        className="flex w-full items-center justify-between rounded-md px-3 py-3 text-left text-base font-semibold"
                      >
                        <span>{slice.primary?.label}</span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : "rotate-0"}`}
                          aria-hidden={true}
                        >
                          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={`${slice.id ?? "item"}-${index}`} className="">
                    <PrismicLink
                      field={slice.primary?.link}
                      className="body-xs bg-bleu-100 text-bleu-950 px-4 py-2 font-medium w-full block rounded-sm"
                      onClick={closeMobileMenu}
                    />
                  </li>
                );
              })}
              <li className="">
                <PrismicLink
                  field={cta}
                  className="body-xs bg-bleu-100 text-bleu-950 px-4 py-2 font-medium w-full block rounded-sm"
                  onClick={closeMobileMenu}
                />
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
}
